/**
 * CompleteTemplatePanel — E3 · "Completar plantilla".
 * Lista los huecos del timeline (vacíos y llenos) y las variables de texto.
 * Llenar un hueco usa la regla `trim`: nunca cambia la velocidad del clip y
 * avisa si el material dura menos que el hueco.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  X, Loader2, AlertTriangle, CheckCircle, FolderOpen, Trash2, Film, Music,
  Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { galleryService } from '../../services/galleryService';
import { templateService } from '../../services/templateService';

const KIND_ICON = { video: Film, image: ImageIcon, audio: Music };

const AssetPicker = ({ kind, onPick, onClose }) => {
  const [assets, setAssets] = useState(null);
  useEffect(() => {
    galleryService.listAssets({ assetType: kind, limit: 60 })
      .then((data) => setAssets(data.assets || []))
      .catch(() => setAssets([]));
  }, [kind]);

  return (
    <div className="tl-template-gallery" data-testid="tl-slot-picker">
      <div className="tl-template-gallery-head">
        <strong>Elegí un {kind === 'audio' ? 'audio' : kind === 'image' ? 'imagen' : 'video'}</strong>
        <button className="tl-icon-btn" onClick={onClose}><X size={13} /></button>
      </div>
      <div className="tl-gallery-grid">
        {assets === null && <div className="tl-gallery-empty"><Loader2 size={16} className="spin" /></div>}
        {assets?.length === 0 && <div className="tl-gallery-empty">No hay assets de este tipo.</div>}
        {assets?.map((a) => (
          <button
            key={a.id} className="tl-gallery-card"
            onClick={() => onPick(a)}
            data-testid={`tl-slot-asset-${a.id}`}
          >
            {(a.media_kind || a.asset_type) === 'image'
              ? <img src={a.url} alt={a.filename} loading="lazy" />
              : a.thumbnail_url
                ? <img src={a.thumbnail_url} alt={a.filename} loading="lazy" />
                : <div className="tl-gallery-audio-icon"><Film size={22} /></div>}
            <span className="tl-gallery-filename">{a.filename}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const CompleteTemplatePanel = ({ projectId, onClose, onChanged }) => {
  const [state, setState] = useState(null);
  const [picking, setPicking] = useState(null);     // slot
  const [busy, setBusy] = useState('');
  const [texts, setTexts] = useState({});

  const load = useCallback(async () => {
    try {
      const data = await templateService.getSlots(projectId);
      setState(data);
      setTexts(Object.fromEntries((data.text_variables || []).map(
        (v) => [v.var_id, v.value ?? v.default_text ?? ''])));
    } catch (_e) {
      setState({ slots: [], text_variables: [], error: true });
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const fill = async (slot, asset) => {
    setPicking(null);
    setBusy(slot.slot_id);
    try {
      const out = await templateService.fillSlot(projectId, slot.slot_id, {
        source_url: asset.url,
        kind: asset.media_kind || asset.asset_type,
        duration: asset.metadata?.duration || asset.duration || null,
        asset_id: asset.id,
        label: asset.filename,
        thumb_url: asset.thumbnail_url || null,
      }, state?.version ?? null);
      if (out.result?.warning) toast.warning(out.result.warning);
      else toast.success('Hueco completado');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || e.response?.data?.detail
        || 'No se pudo completar el hueco');
    } finally { setBusy(''); }
  };

  const clear = async (slot) => {
    setBusy(slot.slot_id);
    try {
      await templateService.clearSlot(projectId, slot.slot_id, state?.version ?? null);
      await load();
      onChanged?.();
    } catch (_e) {
      toast.error('No se pudo vaciar el hueco');
    } finally { setBusy(''); }
  };

  const saveText = async (variable, value) => {
    try {
      await templateService.setText(projectId, variable.var_id, value, state?.version ?? null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar el texto');
    }
  };

  if (!state) {
    return (
      <div className="tl-gallery-overlay" onClick={onClose}>
        <div className="tl-template-modal" onClick={(e) => e.stopPropagation()}>
          <div className="tl-gallery-empty"><Loader2 size={18} className="spin" /></div>
        </div>
      </div>
    );
  }

  const slots = state.slots || [];
  const empty = slots.filter((s) => !s.clip_id).length;

  return (
    <div className="tl-gallery-overlay" onClick={onClose} data-testid="tl-complete-overlay">
      <div className="tl-template-modal" onClick={(e) => e.stopPropagation()} data-testid="tl-complete-panel">
        <div className="tl-gallery-header">
          <span className="tl-gallery-title">Completar plantilla</span>
          <button className="tl-icon-btn" onClick={onClose} data-testid="tl-complete-close">
            <X size={14} />
          </button>
        </div>

        <div className="tl-template-body">
          {slots.length === 0 && (state.text_variables || []).length === 0 && (
            <div className="tl-gallery-empty" data-testid="tl-complete-empty">
              Este timeline no viene de una plantilla con huecos.
            </div>
          )}

          {slots.length > 0 && (
            <div className="tl-template-section">
              <h4>
                Huecos {empty > 0
                  ? <em className="tl-slot-pending" data-testid="tl-complete-pending">{empty} sin completar</em>
                  : <em className="tl-slot-ok"><CheckCircle size={11} /> todos completos</em>}
              </h4>
              <div className="tl-slot-list" data-testid="tl-complete-slot-list">
                {slots.map((slot) => {
                  const kind = (slot.accepts || ['video'])[0];
                  const Icon = KIND_ICON[kind] || Film;
                  return (
                    <div
                      className={`tl-slot-row ${slot.clip_id ? 'filled' : 'empty'}`}
                      key={slot.slot_id}
                      data-testid={`tl-slot-${slot.slot_id}`}
                    >
                      <Icon size={13} />
                      <span className="tl-slot-main">
                        <strong>{slot.name}</strong>
                        <small>
                          {kind} · {Number(slot.duration || 0).toFixed(1)} s · desde{' '}
                          {Number(slot.start || 0).toFixed(1)} s
                        </small>
                        {slot.filled_warning && (
                          <em className="tl-slot-warn" data-testid={`tl-slot-warn-${slot.slot_id}`}>
                            <AlertTriangle size={10} /> {slot.filled_warning}
                          </em>
                        )}
                      </span>
                      <button
                        onClick={() => setPicking(slot)} disabled={busy === slot.slot_id}
                        data-testid={`tl-slot-pick-${slot.slot_id}`}
                      >
                        {busy === slot.slot_id
                          ? <Loader2 size={11} className="spin" />
                          : <FolderOpen size={11} />}
                        {slot.clip_id ? 'Cambiar' : 'Elegir asset'}
                      </button>
                      {slot.clip_id && (
                        <button
                          className="danger" onClick={() => clear(slot)}
                          data-testid={`tl-slot-clear-${slot.slot_id}`}
                        >
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(state.text_variables || []).length > 0 && (
            <div className="tl-template-section">
              <h4>Textos de la plantilla</h4>
              <div className="tl-slot-list" data-testid="tl-complete-var-list">
                {state.text_variables.map((v) => (
                  <label className="tl-var-row" key={v.var_id}>
                    <span>{v.name}</span>
                    <input
                      type="text"
                      value={texts[v.var_id] ?? ''}
                      maxLength={v.max_chars || undefined}
                      onChange={(e) => setTexts((p) => ({ ...p, [v.var_id]: e.target.value }))}
                      onBlur={(e) => saveText(v, e.target.value)}
                      data-testid={`tl-var-input-${v.var_id}`}
                    />
                    {v.max_chars && (
                      <small>{(texts[v.var_id] || '').length}/{v.max_chars}</small>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {picking && (
          <AssetPicker
            kind={(picking.accepts || ['video'])[0]}
            onPick={(asset) => fill(picking, asset)}
            onClose={() => setPicking(null)}
          />
        )}
      </div>
    </div>
  );
};

export default CompleteTemplatePanel;
